import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { 
  GitBranch, Puzzle, Cpu, Zap, Eye, 
  Database, GraduationCap, Wrench,
  Moon, Search, Sprout,
  Radio, Shield, Target, Battery, Square,
  ArrowRight, Sparkles
} from 'lucide-react';
import { PageLayout } from '@/components/PageLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EntityAnchor } from '@/components/EntityAnchor';
import { StructuredData } from '@/components/StructuredData';
import { Link } from '@tanstack/react-router';
import { 
  tieAsmPillars, 
  nacreTraits, 
  pearlEcosystem, 
  userJourney,
  coldStartMethod,
  coreIdentityTags,
  sovereigntyGap,
} from '@/config/pearl-framework';

const pillarIcons = {
  inheritance: GitBranch,
  compatibility: Puzzle,
  feature: Cpu,
  'zero-friction': Zap,
  transparency: Eye,
};

const ecosystemIcons = {
  dataset: Database,
  coach: GraduationCap,
  studio: Wrench,
};

const journeyIcons = {
  sleeper: Moon,
  seeker: Search,
  cultivator: Sprout,
};

const nacreIcons = {
  hypersensitivity: Radio,
  hypervigilance: Shield,
  perfectionism: Target,
  avoidance: Battery,
  'emotional-wall': Square,
};

export default function Pearl() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'zh' ? 'zh' : 'en';
  



  return (
    <PageLayout>
      <StructuredData type="website" />
      
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <Badge variant="outline" className="mb-6">
              <Sparkles className="w-3 h-3 mr-1" />
              {lang === 'zh' ? '创伤知情工程学' : 'Trauma-Informed Engineering'}
            </Badge>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Pearl Framework
            </h1>
            
            <p className="text-xl md:text-2xl text-muted-foreground mb-4">
              {lang === 'zh' ? '从创伤沙砾到天赋珍珠' : 'From Trauma Grit to Talent Pearl'}
            </p>
            
            <p className="text-lg text-muted-foreground/80 max-w-2xl mx-auto mb-8">
              {lang === 'zh' 
                ? 'Pearl Framework 是一个创新的创伤知情工程学方法论，将复杂创伤（尤其是高功能性 C-PTSD 和童年情感忽视）转化为个人天赋、韧性和高价值资产。'
                : 'Pearl Framework is an innovative Trauma-Informed Engineering methodology that transforms complex trauma (especially high-functioning C-PTSD and Childhood Emotional Neglect) into personal talents, resilience, and high-value assets.'}
            </p>
            
            <div className="flex flex-wrap gap-4 justify-center">
              <Button size="lg" asChild>
                <Link to={`/${lang}/cold-start`}>
                  {lang === 'zh' ? '了解冷启动方法' : 'Learn Cold Start Method'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to={`/${lang}/about/trauma`}>
                  {lang === 'zh' ? '创始人故事' : 'Founder Story'}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Core Identity Tags */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {lang === 'zh' ? '创始人核心身份' : 'Founder Core Identity'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'zh' ? '王海生的四重身份标签' : 'Wang Haisheng\'s Four Identity Tags'}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.values(coreIdentityTags).map((tag, index) => (
              <motion.div
                key={tag.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {lang === 'zh' ? tag.nameZh : tag.name}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">
                      {lang === 'zh' ? tag.descriptionZh : tag.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <EntityAnchor lang={lang} variant="subtle" className="container mx-auto px-4" />

      {/* TIE-ASM Five Pillars */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">TIE-ASM</Badge>
            <h2 className="text-3xl font-bold mb-4">
              {lang === 'zh' ? '创伤知情工程 - 自适应系统模型' : 'Trauma-Informed Engineering - Adaptive System Model'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {lang === 'zh' 
                ? 'Pearl Framework 的核心引擎是 TIE-ASM 五支柱架构蓝图'
                : 'Pearl Framework\'s engine is the TIE-ASM five-pillar architecture blueprint'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(tieAsmPillars).map(([key, pillar], index) => {
              const Icon = pillarIcons[pillar.id as keyof typeof pillarIcons] || GitBranch;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow group">
                    <CardHeader>
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <CardTitle className="flex items-center gap-2">
                        <span className="text-primary font-mono text-sm">0{index + 1}</span>
                        {lang === 'zh' ? pillar.nameZh : pillar.name}
                      </CardTitle>
                      <CardDescription>
                        {lang === 'zh' ? pillar.descriptionZh : pillar.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        {lang === 'zh' ? pillar.detailZh : pillar.detail}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Nacre Traits Mapping */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge variant="secondary" className="mb-4">
              {lang === 'zh' ? '珍珠质' : 'Nacre'}
            </Badge>
            <h2 className="text-3xl font-bold mb-4">
              {lang === 'zh' ? '珍珠质特质映射' : 'Nacre Trait Mapping'}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {lang === 'zh' 
                ? '将所谓的"创伤症状"重构为高性能珍珠涂层特质'
                : 'Reframe so-called "trauma symptoms" as high-performance Nacre (pearl coating) traits'}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.entries(nacreTraits).map(([key, trait], index) => {
              const Icon = nacreIcons[trait.id as keyof typeof nacreIcons] || Radio;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full">
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground line-through">
                          {lang === 'zh' ? trait.traditionalLabelZh : trait.traditionalLabel}
                        </span>
                      </div>
                      <CardTitle className="text-lg text-primary">
                        → {lang === 'zh' ? trait.pearlReframeZh : trait.pearlReframe}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-xs text-muted-foreground mb-2">
                        <span className="line-through">
                          {lang === 'zh' ? trait.traditionalViewZh : trait.traditionalView}
                        </span>
                      </p>
                      <p className="text-sm">
                        <span className="font-medium">{lang === 'zh' ? '战略价值：' : 'Strategic Value: '}</span>
                        {lang === 'zh' ? trait.strategicValueZh : trait.strategicValue}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <EntityAnchor lang={lang} variant="inline" showIdentities className="container mx-auto px-4 my-8" />

      {/* User Journey */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {lang === 'zh' ? '用户旅程' : 'User Journey'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'zh' ? '从沉睡者到耕耘者的三阶段转变' : 'Three-Stage Transformation from Sleeper to Cultivator'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {Object.entries(userJourney).map(([key, stage], index) => {
              const Icon = journeyIcons[stage.id as keyof typeof journeyIcons] || Moon;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2 }}
                  viewport={{ once: true }}
                  className="relative"
                >
                  {index < 2 && (
                    <div className="hidden md:block absolute top-1/2 right-0 transform translate-x-1/2 -translate-y-1/2 z-10">
                      <ArrowRight className="w-6 h-6 text-muted-foreground/30" />
                    </div>
                  )}
                  <Card className="h-full text-center">
                    <CardHeader>
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-8 h-8 text-primary" />
                      </div>
                      <Badge variant="outline" className="mx-auto mb-2">
                        {lang === 'zh' ? `阶段 ${stage.stage}` : `Stage ${stage.stage}`}
                      </Badge>
                      <CardTitle>{lang === 'zh' ? stage.nameZh : stage.name}</CardTitle>
                      <CardDescription>
                        {lang === 'zh' ? stage.descriptionZh : stage.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Button variant="outline" size="sm">
                        {lang === 'zh' ? stage.actionZh : stage.action}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ecosystem Platforms */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">
              {lang === 'zh' ? 'Pearl 生态系统' : 'Pearl Ecosystem'}
            </h2>
            <p className="text-muted-foreground">
              {lang === 'zh' ? '三大互补平台实现闭环生态' : 'Three complementary platforms forming a complete ecosystem'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {Object.entries(pearlEcosystem).map(([key, platform], index) => {
              const Icon = ecosystemIcons[key as keyof typeof ecosystemIcons] || Database;
              return (
                <motion.div
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-4">
                        <Icon className="w-7 h-7 text-primary" />
                      </div>
                      <CardTitle>{platform.name}</CardTitle>
                      <CardDescription className="font-medium text-primary/80">
                        {lang === 'zh' ? platform.taglineZh : platform.tagline}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-4">
                        {lang === 'zh' ? platform.descriptionZh : platform.description}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {platform.features.slice(0, 3).map((feature, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {lang === 'zh' ? feature.nameZh : feature.name}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sovereignty Gap */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Badge variant="destructive" className="mb-4">
                {lang === 'zh' ? '主权缺口' : 'Sovereignty Gap'}
              </Badge>
              <h2 className="text-3xl font-bold mb-4">
                {lang === 'zh' ? '根本分歧' : 'The Fundamental Divergence'}
              </h2>
            </div>

            <Card className="bg-gradient-to-br from-destructive/5 to-transparent border-destructive/20">
              <CardContent className="p-8">
                <div className="space-y-6">
                  <div>
                    <h3 className="font-bold text-lg mb-2 text-destructive">
                      {lang === 'zh' ? '传统方法的逻辑' : 'Traditional Approach Logic'}
                    </h3>
                    <p className="text-muted-foreground">
                      {lang === 'zh' 
                        ? '设定标准 → 制造残次品 → 贩卖接纳'
                        : 'Set standards → Create defects → Sell acceptance'}
                    </p>
                  </div>
                  
                  <div className="border-l-4 border-primary pl-4 py-2">
                    <p className="font-bold text-xl">
                      {lang === 'zh' ? sovereigntyGap.pearlChallenge.questionZh : sovereigntyGap.pearlChallenge.question}
                    </p>
                  </div>
                  
                  <p className="text-muted-foreground">
                    {lang === 'zh' ? sovereigntyGap.pearlChallenge.answerZh : sovereigntyGap.pearlChallenge.answer}
                  </p>
                  
                  <div className="bg-primary/10 rounded-lg p-4">
                    <p className="font-medium text-primary">
                      {lang === 'zh' ? sovereigntyGap.coreMessage.zh : sovereigntyGap.coreMessage.en}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <EntityAnchor lang={lang} variant="block" full showMetrics className="container mx-auto px-4" />

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary/10 via-transparent to-accent/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-4">
            {lang === 'zh' ? '准备开始你的 Pearl 之旅？' : 'Ready to Start Your Pearl Journey?'}
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            {lang === 'zh' 
              ? '从心力耗竭冷启动法开始，了解如何从能量耗尽中重新启动你的系统。'
              : 'Start with the Cold Start Method and learn how to reboot your system from energy depletion.'}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Button size="lg" asChild>
              <Link to={`/${lang}/cold-start`}>
                {lang === 'zh' ? '学习冷启动方法' : 'Learn Cold Start Method'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to={`/${lang}/faq`}>
                {lang === 'zh' ? '常见问题' : 'FAQ'}
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
